/* eslint-disable react/function-component-definition */
import * as React from 'react';
import { useIntl } from 'react-intl';
import { useLocation, DefaultParams } from 'wouter';
import { useSetRecoilState } from 'recoil';
import TableRow from './TableRow';
import TableService, {
  TABLE_GDPR_PROFILES,
  TABLE_OPERATING_COSTS_TYPES,
  TABLE_REFUELING,
  TABLE_SUPPLIER,
} from '../../../Services/TableService';
import useUrlQueryParams from '../../../hooks/useUrlQueryParams';
import { setLoaderAtom } from '../../../store/recoil/AppState';

interface ITableBodyProps {
  tableName: string;
  data: any;
  actionSave: any;
  buttons: any;
  dateFormat: string;
  isNavigatorDisabled?: boolean;
  handleForm?: any;
  openForm?: any;
  reportName?: string;
  actionsInMenu?: boolean;
  routerParams?: DefaultParams | null;
}

interface ITableSummaryTdProps {
  i: string;
  title: any;
  data: any;
}

function TableSummaryTd(props: ITableSummaryTdProps): JSX.Element {
  const isNotTimestampFirst = !!(props && props.i === 'timestamp' && !props.title.title);
  return (
    props &&
    !isNotTimestampFirst &&
    (props.title.title ? (
      <td colSpan={props.i === 'timestamp' ? 1 : 2} className="font-weight-bold text-black">
        <strong>{props.title.title}</strong>
      </td>
    ) : (
      <td style={{ textAlign: 'right' }}>
        <strong>{props.title}</strong>
      </td>
    ))
  );
}

export default function TableBody(props: ITableBodyProps): JSX.Element {
  const { formatMessage: f } = useIntl();
  const setLoader = useSetRecoilState<boolean>(setLoaderAtom);
  const [noDataTitleWidth, setNoDataTitleWidth] = React.useState<number>(0);
  const [pathname, navigate] = useLocation();

  const {
    columns,
    records,
    summary,
    actions,
    userSettingsKey,
    rowHeight,
    activeRows,
    multipleActionsAllowed,
  } = props.data;
  const queryUrl = useUrlQueryParams();
  const visibleColumns = Object.keys(columns)
    .filter((j) => columns[j].system === false)
    .filter((j) => columns[j].visible === true);
  let isWithActions = false;
  let moreThanTwoActions = false;
  if (props.actionsInMenu) {
    moreThanTwoActions = true;
  } else if (records.length) {
    records.forEach((i: any) => {
      if (i.actions) {
        if (i.actions.length > 0) {
          isWithActions = true;
        }
        if (i.actions.length > 2) {
          moreThanTwoActions = true;
        }
      }
    });
  }

  const getIsEditableModal = React.useCallback(() => {
    if (props.routerParams && props.tableName !== TABLE_REFUELING) {
      return true;
    }
    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const handleFormGenerationFromUrl = async (id: any, customerId: string | null) => {
    if (props.tableName === TABLE_GDPR_PROFILES) {
      return;
    }
    if (props.handleForm) {
      setLoader(true);
      const response = await TableService.getFormData(props.tableName, id, Number(customerId));

      if (response && response.status === 'ok') {
        const inputs = [...response.form.inputs];
        props.handleForm(inputs, id, customerId || null);
      }
      setLoader(false);
    }
  };

  const handleEditModal = () => {
    // const customerId = Number(search.split('=').pop());
    const customerId = queryUrl.get('customerId');
    if (props.routerParams) {
      if (
        props.tableName === TABLE_SUPPLIER ||
        props.tableName === TABLE_OPERATING_COSTS_TYPES ||
        props.tableName === TABLE_GDPR_PROFILES
      ) {
        props.handleForm(props.routerParams.id, true, customerId || null);
      } else if (props.openForm) {
        props.openForm(props.routerParams.id, customerId || null);
      } else {
        handleFormGenerationFromUrl(props.routerParams.id, customerId || null);
      }
    }
  };

  React.useEffect(() => {
    if (getIsEditableModal()) {
      handleEditModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getIsEditableModal]);

  React.useEffect(() => {
    if (records.length === 0) {
      const element = document.getElementById('tableWrapper');
      if (element && element.offsetWidth !== 0) {
        setNoDataTitleWidth(element.offsetWidth);
      }
    }
  }, [records]);

  return (
    <tbody>
      {Object.keys(records).map((i, count) => (
        <TableRow
          key={i}
          firstItems={Number(i) < 4}
          lastItems={records.length - 5 < Number(i)}
          lastItem={records.length - 2 < Number(i)}
          id={records[i].id}
          tableName={props.tableName}
          multipleActions={actions.multipleActions}
          multipleActionsAllowed={multipleActionsAllowed}
          activeRows={activeRows}
          data={records[i]}
          rowHeight={rowHeight || false}
          columns={columns}
          userSettingsKey={userSettingsKey}
          dateFormat={props.dateFormat}
          actionSave={props.actionSave}
          buttons={props.buttons}
          moreThanTwoActions={moreThanTwoActions}
          records={records}
          countRow={count}
          isNavigatorDisabled={props.isNavigatorDisabled}
          handleForm={props.handleForm}
          openForm={props.openForm}
          reportName={props.reportName}
        />
      ))}
      {records.length === 0 && (
        <tr className="hover-off border-off background-off">
          <td style={{ background: '#fff' }} colSpan={Object.keys(columns).length + 1}>
            <div
              className="text-center py-8 d-flex align-items-center d-flex align-items-center"
              style={{ minHeight: 'calc(100% - 50px)' }}
            >
              <div
                style={{ minHeight: 'calc(100% - 50px)', width: `${noDataTitleWidth}px` }}
                className="text-center py-8 align-items-center align-items-center"
              >
                <div className="my-8 py-8">
                  <div
                    className="col-12 col-md-8 mx-auto fs-7 text-gray-light"
                    style={{ position: 'static' }}
                    data-cy={`table-${props.tableName}-no-data`}
                  >
                    {f({ id: 'table.noData.title' })}
                    <br />
                    {props.isNavigatorDisabled === true
                      ? f({ id: 'table.noDataWithoutNavigator' })
                      : f({ id: 'table.noData.text' })}
                  </div>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}

      {records.length > 0 &&
        summary &&
        Object.values(summary).map((row: any) => (
          <tr key={`table-summary-${row.title}`} className="bg-gray-light tableSummary">
            {actions.multipleActions.length > 0 && multipleActionsAllowed && <td />}
            {visibleColumns.map((i) => (
              <TableSummaryTd
                key={`summary-td-${i}`}
                i={i}
                title={
                  row.columns.filter((k: any) => k.name === i)[0]
                    ? row.columns.filter((k: any) => k.name === i)[0].value
                    : visibleColumns[0] === i
                      ? { title: row.title }
                      : ''
                }
                data={columns[i]}
              />
            ))}
            {isWithActions && <td colSpan={2} />}
          </tr>
        ))}
    </tbody>
  );
}
